"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const gsap_1 = __importDefault(require("gsap"));
class Flow {
    main_element;
    flow_body;
    steps = [];
    currentStep = 0;
    random = Math.random();
    prevButton = null;
    nextButton = null;
    onSubmit = null;
    onSubmitText = 'Submitting...';
    constructor(ele) {
        let element;
        if (ele instanceof HTMLElement) {
            element = ele;
        }
        else {
            const e = document.querySelector(ele) || jQuery(ele)[0] || null;
            if (e instanceof HTMLElement)
                element = e;
            else
                throw new Error('Invalid element');
        }
        this.main_element = element;
        this.main_element.flow_instance = this;
        this.#init();
        this.#initListeners();
    }
    #initListeners() {
        window.addEventListener('resize', () => {
            const currentStep = this.currentStep;
            this.setStep(currentStep);
        });
    }
    #init() {
        const flow_body = document.createElement('div');
        flow_body.classList.add('flow-body');
        this.flow_body = flow_body;
        this.main_element.appendChild(flow_body);
        this.#makeSteps();
        let buttonNav = this.main_element.querySelector('.flow-btn-nav');
        if (buttonNav) {
            this.#createButtonNav(buttonNav);
        }
        else {
            buttonNav = this.#createNavigation();
        }
        this.main_element.appendChild(buttonNav);
        if (this.main_element.dataset.flowSubmit) {
            const func = window[this.main_element.dataset.flowSubmit];
            this.onSubmit = func.bind(this);
        }
        if (this.main_element.dataset.flowSubmitText) {
            this.onSubmitText = this.main_element.dataset.flowSubmitText;
        }
        this.setStep(0);
    }
    #makeSteps(elements = this.main_element.querySelectorAll('.flow-step')) {
        this.flow_body.innerHTML = '';
        this.steps = [];
        Array.from(elements).forEach((step, index) => {
            const stepInstance = step;
            stepInstance.setAttribute('data-index', index.toString());
            stepInstance.flow_step = {
                index: index,
            };
            if (stepInstance.dataset.flowPromise) {
                const func = window[stepInstance.dataset.flowPromise];
                stepInstance.flow_step.completionPromise = func.bind(this, stepInstance);
            }
            if (stepInstance.dataset.flowPromiseText) {
                stepInstance.flow_step.completionPromiseText = stepInstance.dataset.flowPromiseText;
            }
            this.flow_body.appendChild(stepInstance);
            this.steps.push(stepInstance);
        });
    }
    #createButtonNav(buttonDiv) {
        this.prevButton = buttonDiv.querySelector('.flow-prev');
        this.nextButton = buttonDiv.querySelector('.flow-next');
        if (this.nextButton)
            this.nextButton.dataset.flowText = this.nextButton.textContent || '';
        if (this.prevButton)
            this.prevButton.dataset.flowText = this.prevButton.textContent || '';
        this.prevButton?.addEventListener('click', () => {
            this.setStep(this.currentStep - 1);
        });
        this.nextButton?.addEventListener('click', () => {
            this.setStep(this.currentStep + 1);
        });
        return buttonDiv;
    }
    #createNavigation() {
        const navigation = document.createElement('div');
        navigation.classList.add('flow-navigation');
        const previous = document.createElement('button');
        previous.classList.add('flow-previous');
        previous.textContent = 'Previous';
        previous.addEventListener('click', () => {
            this.setStep(this.currentStep - 1);
        });
        const next = document.createElement('button');
        next.classList.add('flow-next');
        next.textContent = 'Next';
        next.addEventListener('click', () => {
            this.setStep(this.currentStep + 1);
        });
        navigation.appendChild(previous);
        navigation.appendChild(next);
        return navigation;
    }
    currentHeightObserver = null;
    #submit = async () => {
        if (this.onSubmit) {
            const w = window;
            let t = null;
            w.__.loaderButtonInit(this.nextButton);
            try {
                t = await w.toast.promise(this.onSubmit(), {
                    loading: this.onSubmitText ?? 'Please wait...',
                    success: x => x ?? 'Success',
                    error: x => x ?? 'Error',
                });
            }
            catch (e) {
                console.error(e);
            }
            finally {
                this.nextButton.finishLoading();
                if (t)
                    w.toast.dismiss(t);
            }
        }
    };
    async setStep(index) {
        if (this.currentHeightObserver) {
            this.currentHeightObserver.disconnect();
            this.currentHeightObserver = null;
        }
        const activeElement = this.steps[index];
        if (!(await this.#checkValidity(index)))
            return;
        if (index === 0) {
            this.prevButton?.classList.add('disabled');
        }
        else {
            this.prevButton?.classList.remove('disabled');
        }
        if (index === this.steps.length - 1) {
            if (this.nextButton) {
                this.nextButton.textContent = this.nextButton.dataset.flowFinish ?? 'Finish';
            }
            if (this.onSubmit) {
                this.nextButton?.addEventListener('click', this.#submit);
            }
        }
        else if (this.nextButton) {
            this.nextButton.textContent = this.nextButton?.dataset.flowText;
            this.nextButton?.removeEventListener('click', this.#submit);
        }
        this.steps.forEach((step) => {
            if (step !== activeElement) {
                gsap_1.default.to(step, { duration: 0.5, opacity: 0 });
            }
            else {
                gsap_1.default.to(step, { duration: 1, opacity: 1, ease: 'power2' });
            }
        });
        activeElement.classList.add('active');
        this.#scrollToElement(activeElement).then(() => {
            this.currentHeightObserver = new ResizeObserver((entries) => {
                const height = entries[0].contentRect.height;
                gsap_1.default.to(this.flow_body, { duration: 0.01, height: height });
            });
            this.currentHeightObserver.observe(activeElement);
        });
        this.currentStep = index;
    }
    async #checkValidity(index) {
        const activeElement = this.steps[index];
        const prevStep = this.currentStep;
        const prevElement = this.steps[prevStep];
        const w = window;
        let t = null;
        if (!activeElement)
            return false;
        if (index > prevStep) {
            const prevStepInstance = this.steps[prevStep];
            if (prevStepInstance.flow_step?.completionPromise) {
                const p = prevStepInstance.flow_step.completionPromise();
                w.__.loaderButtonInit(this.nextButton);
                try {
                    t = await window.toast.promise(p, {
                        loading: prevStepInstance.flow_step.completionPromiseText ?? 'Please wait...',
                        success: x => x ?? 'Success',
                        error: x => x ?? 'Error',
                    });
                }
                catch (e) {
                    console.error(e);
                    return false;
                }
                finally {
                    this.nextButton.finishLoading();
                    if (t)
                        w.toast.dismiss(t);
                }
            }
        }
        return true;
    }
    #scrollToElement(element) {
        const promise = new Promise((resolve, reject) => {
            const height = element.clientHeight;
            const width = element.clientWidth;
            gsap_1.default.to(this.flow_body, {
                duration: 0.5, scrollTo: element, ease: 'power2', height: height, onComplete: () => {
                    resolve();
                }
            });
        });
        return promise;
    }
    static initFlow() {
        const elements = document.querySelectorAll('.flow');
        elements?.forEach((element) => {
            new Flow(element);
        });
    }
}
exports.default = Flow;
